import type { TFunction } from 'i18next';
import { formatElapsed } from 'utils/elapsed';
import { isDeadlinePassed, parisDateTime, parisDayOf, parisToday } from 'utils/interventionDate';
import type { InterventionRequest } from 'api/interventionRequestsApi';
import { followUpRowStatusLabel, solicitationPhrase } from 'pages/AdminInterventionRequests/vocabulary';
import { refusalDetail, technicianName } from './upcomingAssignment';

/** A solicitation row as both drawers read it (ticket screen and follow-up): E6, E15, E18. */

const MAX_REMINDERS = 3;
const HOUR_S = 3600;

/** What a pending row offers instead of, or beside, « Relancer » — derived, never stored (E18); CA14 first. */
export type RemindState = 'open' | 'cooling' | 'capped' | 'deadlinePassed';

/** F17 — the cooldown is the server's own `remindableAt`; `null` means a manual reminder is possible now. */
export const remindState = (request: InterventionRequest, now: number): RemindState => {
  if (isDeadlinePassed(request.deadlineDate)) return 'deadlinePassed';
  if (request.reminderCount >= MAX_REMINDERS) return 'capped';
  const remindableAt = request.remindableAt ? Date.parse(request.remindableAt) : NaN;
  return !Number.isNaN(remindableAt) && now < remindableAt ? 'cooling' : 'open';
};

/** The sentence under the name: the wait and the reminders on a pending row, the outcome otherwise. */
export const rowPhrase = (t: TFunction, request: InterventionRequest, acceptor: InterventionRequest | null): string => {
  if (request.status === 'pending') {
    if (request.reminderCount > 0) {
      const last = request.lastReminderAt ? parisDateTime(request.lastReminderAt) : { date: '—', time: '—' };
      return `${t('Reminded {{count}} time', { count: request.reminderCount })} · ${t('last reminder {{date}} at {{time}}', last)}`;
    }
    const elapsed = request.elapsedSeconds < HOUR_S ? t('just now') : formatElapsed(request.elapsedSeconds);
    return `${t('Sent {{elapsed}}', { elapsed })} · ${t('never reminded')}`;
  }
  if (request.status === 'declined') {
    const reason = refusalDetail(t, request);
    if (reason) return t('Reason: {{reason}}', { reason });
  }
  if (request.status === 'superseded' && acceptor?.respondedAt) {
    return t('Mission taken by {{name}} on {{date}}', {
      name: technicianName(acceptor),
      date: parisDateTime(acceptor.respondedAt).date,
    });
  }
  return solicitationPhrase(t, request, acceptor ? technicianName(acceptor) : null);
};

/** The row's pill: the cap reached replaces the status, else the derived one the follow-up shows (E18). */
export const rowStatusLabel = (t: TFunction, request: InterventionRequest, remind: RemindState | null): string =>
  remind === 'capped'
    ? t('Reminded ×{{count}}', { count: request.reminderCount })
    : followUpRowStatusLabel(t, request);

/** « Relance possible aujourd'hui / demain à {heure} », read off the server's `remindableAt` (F17). */
export const nextReminderText = (t: TFunction, remindableAt: string): string => {
  const { time } = parisDateTime(remindableAt);
  return parisDayOf(new Date(remindableAt)) === parisToday()
    ? t('Next reminder possible today at {{time}}', { time })
    : t('Next reminder possible tomorrow at {{time}}', { time });
};

/**
 * The line under a pending row that explains or replaces « Relancer »; a view-only reader gets the
 * cooldown only. `deadlineEditable` false (desk without the workflow, or unknown): cancel and resolicit.
 */
export const remindHint = (
  t: TFunction,
  request: InterventionRequest,
  remind: RemindState | null,
  canAct: boolean,
  deadlineEditable: boolean
): { text: string; tone: string } | null => {
  if (!canAct && remind !== 'cooling') return null;
  switch (remind) {
    case 'cooling':
      return request.remindableAt ? { text: nextReminderText(t, request.remindableAt), tone: 'text-muted-foreground' } : null;
    case 'capped':
      return {
        text: t('Cap reached · 3 reminders without an answer — solicit another technician'),
        tone: 'text-amber-700 dark:text-amber-400',
      };
    case 'deadlinePassed':
      return {
        text: deadlineEditable
          ? t('Deadline passed — change the deadline or cancel.')
          : t('Deadline passed — cancel, then solicit again.'),
        tone: 'text-rose-600 dark:text-rose-400',
      };
    default:
      return null;
  }
};
